import { useEffect } from "react";
import { Outlet } from "react-router-dom";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useEventModal } from "@/hooks/useEventModal";
import { EventDetailSheet } from "@/components/EventDetailSheet";
import { CommandModal } from "@/components/CommandModal";
import {
  useCommandModalStore,
  selectCommandModalOpen,
  selectCommandAutoListen,
  selectOpenCommandModal,
  selectCloseCommandModal,
} from "@/stores";
import { useRememberVisit } from "@/hooks/useRememberVisit";
import { BottomTabBar } from "./BottomTabBar";

/**
 * 인증된 화면 전체를 감싸는 셸.
 *
 * **이름과 달리 데스크톱도 여기를 지난다** — 모바일에서만 하단 탭 바를 더할 뿐이다.
 * 높이의 주인이자 이벤트 상세의 마운트 지점이라 데스크톱을 빼놓을 수 없다.
 */
export function MobileShell() {
  const isMobile = useIsMobile();
  useRememberVisit();

  /*
    이벤트 상세를 여기서 그린다 — 여는 쪽이 전역이기 때문이다. 헤더 알람 인박스는
    공용 헤더에, 이벤트 알람 배너는 라우트 밖에 있어 어느 화면에서도 뜨는데,
    상세가 관제에만 있으면 보건관리·CCTV 에서는 주소에 `?event=` 만 붙고 아무 일도
    일어나지 않는다. 인증된 화면 전체를 감싸는 이 셸이 트리거와 같은 층이다.
  */
  const { eventId, closeEvent } = useEventModal();
  const eventSheet = <EventDetailSheet eventId={eventId} onClose={closeEvent} />;

  /*
    명령 팔레트도 같은 이유로 여기 있다 — 여는 쪽(헤더 버튼·단축키)이 전역이다.
    **그리고 인증 안쪽이어야 한다.** App 최상단에 두면 로그인 화면에서도 열려,
    아직 아무 현장에도 접근할 수 없는 사람에게 빈 팔레트가 뜬다.
  */
  const commandOpen = useCommandModalStore(selectCommandModalOpen);
  const autoListen = useCommandModalStore(selectCommandAutoListen);
  const openCommand = useCommandModalStore(selectOpenCommandModal);
  const closeCommand = useCommandModalStore(selectCloseCommandModal);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // metaKey 를 같이 본다 — ctrlKey 만 보면 Mac 에서 Cmd+K 가 안 먹는다.
      if ((event.metaKey || event.ctrlKey) && event.code === "KeyK") {
        event.preventDefault();
        openCommand();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [openCommand]);

  const commandPalette = (
    <CommandModal open={commandOpen} onClose={closeCommand} autoListen={autoListen} />
  );

  /*
    데스크톱에서도 높이를 여기서 준다. 자식이 각자 h-dvh 를 쓰면 모바일에서 그 값이
    하단 탭 바 자리까지 먹어 바가 0 높이로 눌린다 — 실제로 그랬다.
    높이의 주인을 하나로 두고 자식은 h-full 만 쓴다.
  */
  if (!isMobile) {
    return (
      <div className="h-dvh w-full">
        <Outlet />
        {eventSheet}
        {commandPalette}
      </div>
    );
  }

  return (
    <div className="flex h-dvh flex-col bg-[var(--surface-sunken)]">
      <div className="min-h-0 flex-1 overflow-y-auto">
        <Outlet />
      </div>
      <BottomTabBar />
      {eventSheet}
      {commandPalette}
    </div>
  );
}
